'use client'

import { useEffect, useState, useCallback } from 'react'
import {
  Dialog,
  Navigation,
  Cell,
  Text,
  Button,
  Icon,
  Tooltip,
  Divider,
  Spacer,
} from '@vinted/web-ui'
import { Copy24, Envelope24, Home24, X24 } from '@vinted/monochrome-icons'
import { compact } from 'lodash'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { AddressModel } from '@marketplace-web/address/address-data'
import {
  BusinessAccountInvoiceInstructionsModel,
  getBusinessAccountInvoiceInstructions,
  transformBusinessAccountInvoiceInstructionsDto,
} from '@marketplace-web/business-accounts/business-accounts-data'

type Props = {
  transactionId: number | undefined
  isOpen: boolean
  onClose: () => void
}

const BuyerDetailsModal = ({ transactionId, isOpen, onClose }: Props) => {
  const translate = useTranslate('conversation.buyer_details.modal')

  const [buyerDetails, setBuyerDetails] = useState<BusinessAccountInvoiceInstructionsModel | null>(
    null,
  )

  const fetchBuyerDetails = useCallback(async () => {
    if (!transactionId) return

    const response = await getBusinessAccountInvoiceInstructions({ transactionId })

    if ('errors' in response) return

    setBuyerDetails(
      transformBusinessAccountInvoiceInstructionsDto(
        response.business_account_invoice_instructions,
      ),
    )
  }, [transactionId])

  useEffect(() => {
    fetchBuyerDetails()
  }, [fetchBuyerDetails])

  const generateAddressText = ({ name, line1, line2, postalCode, city, country }: AddressModel) =>
    compact([name, line1, line2, postalCode, city, country]).join('\n')

  const handleTooltipClick = (text: string) => () => navigator.clipboard.writeText(text)

  const renderSuffix = (copyText: string) => (
    <Tooltip placement="left" hover={false} content={translate('actions.copy')}>
      <Button
        icon={<Icon name={Copy24} color="greyscale-level-3" />}
        styling="flat"
        onClick={handleTooltipClick(copyText)}
      />
    </Tooltip>
  )

  const renderAddress = () => {
    if (!buyerDetails) return null

    const { address } = buyerDetails
    const { name, line1, line2, postalCode, city, country } = address

    return (
      <div id="address-block">
        <Cell
          prefix={<Icon name={Home24} color="greyscale-level-3" />}
          suffix={renderSuffix(generateAddressText(address))}
          title={translate('labels.address')}
        >
          <Text as="span" width="parent">
            {name}
          </Text>
          <Text as="span" width="parent">
            {line1}
          </Text>
          {line2 && (
            <Text as="span" width="parent">
              {line2}
            </Text>
          )}
          <Text as="span" width="parent">
            {postalCode}, {city}
          </Text>
          <Text as="span" width="parent">
            {country}
          </Text>
        </Cell>
      </div>
    )
  }

  const renderEmail = () => {
    if (!buyerDetails) return null

    const { email } = buyerDetails

    return (
      <Cell
        prefix={<Icon name={Envelope24} color="greyscale-level-3" />}
        suffix={renderSuffix(email)}
        title={translate('labels.email')}
      >
        <div className="u-ellipsis">{email}</div>
      </Cell>
    )
  }

  return (
    <Dialog show={isOpen}>
      <Navigation
        right={<Button styling="flat" onClick={onClose} iconName={X24} inline />}
        body={translate('title')}
      />
      <Divider />
      {renderAddress()}
      {renderEmail()}
      <Spacer size="x4-large" />
    </Dialog>
  )
}

export default BuyerDetailsModal
